import { useState, useEffect } from 'react';
import { Box, Button, Grid, Typography, CircularProgress, IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import useAuth from '../hoc/AuthProvider/useAuth';
import { Permissions } from '../config/permissions';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { useDashboardConfig } from '../hooks/useDashboardConfig';
import ChartWidget from '../components/Dashboard/ChartWidget';
import SortableWidget from '../components/Dashboard/SortableWidget';
import WidgetModal from '../components/Dashboard/WidgetModal';
import { Widget, TimeRange } from '../types/dashboard';

const Dashboard = () => {
  const { config, loading, dirty, dirtyIds, addWidget, updateWidget, removeWidget, reorderWidgets, saveWidget, saveAll } = useDashboardConfig();
  const { havePermission } = useAuth();
  const canEdit = havePermission(Permissions.Dashboard_Update);

  const [modalState, setModalState] = useState<{ open: boolean; widget?: Widget }>({ open: false });
  const [darkMode, setDarkMode] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const handleWidgetSave = (widget: Widget) => {
    if (modalState.widget) updateWidget(widget);
    else addWidget(widget);
    setModalState({ open: false });
    saveWidget(widget.id);
  };

  const handleTimeRangeChange = (widgetId: string, range: TimeRange) => {
    const widget = config.configJson?.widgets.find(w => w.id === widgetId);
    if (widget) updateWidget({ ...widget, timeRange: range });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const widgets = config.configJson?.widgets ?? [];
    const oldIndex = widgets.findIndex(w => w.id === active.id);
    const newIndex = widgets.findIndex(w => w.id === over.id);
    const reordered = arrayMove(widgets, oldIndex, newIndex);
    reorderWidgets(reordered);
    saveAll();
  };

  const bg = darkMode ? '#0f172a' : '#f1f5f9';
  const toolbarBg = darkMode ? '#1e293b' : '#ffffff';
  const borderColor = darkMode ? '#334155' : '#e2e8f0';
  const textColor = darkMode ? '#f1f5f9' : '#1e293b';
  const subtitleColor = darkMode ? '#94a3b8' : '#64748b';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, backgroundColor: bg }}>
        <CircularProgress />
      </Box>
    );
  }

  const widgets = config.configJson?.widgets ?? [];

  return (
    <Box sx={{ backgroundColor: bg, minHeight: '100%', borderRadius: 2, p: { xs: 1.5, md: 2.5 } }}>

      {/* Toolbar */}
      <Box sx={{ backgroundColor: toolbarBg, border: `1px solid ${borderColor}`, borderRadius: 2, px: 2.5, py: 1.5, mb: 2.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={700} color={textColor}>Dashboard</Typography>
          <Typography variant="caption" color={subtitleColor}>
            {widgets.length} grafik · {dirty ? 'Kaydedilmemiş değişiklik var' : 'Kaydedildi'}
          </Typography>
        </Box>

        <Tooltip title={darkMode ? 'Açık Tema' : 'Koyu Tema'}>
          <IconButton onClick={() => setDarkMode(d => !d)} size="small" sx={{ color: subtitleColor }}>
            {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Tooltip>

        {canEdit && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            size="small"
            onClick={() => setModalState({ open: true })}
            sx={{ borderColor, color: textColor, '&:hover': { borderColor: 'primary.main' } }}
          >
            Ekle
          </Button>
        )}
      </Box>

      {/* Grid */}
      {widgets.length === 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: 2, border: `2px dashed ${borderColor}`, borderRadius: 2 }}>
          <Typography color={subtitleColor} variant="body1">Henüz grafik eklenmedi</Typography>
          {canEdit && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setModalState({ open: true })}>
              İlk Grafiği Ekle
            </Button>
          )}
        </Box>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={widgets.map(w => w.id)} strategy={rectSortingStrategy}>
            <Grid container spacing={2}>
              {widgets.map(widget => (
                <Grid size={{ xs: 12, md: 6 }} key={widget.id}>
                  <SortableWidget id={widget.id}>
                    {dragHandleProps => (
                      <ChartWidget
                        widget={widget}
                        darkMode={darkMode}
                        isDirty={dirtyIds.has(widget.id)}
                        dragHandleProps={dragHandleProps}
                        onEdit={() => setModalState({ open: true, widget })}
                        onRemove={() => removeWidget(widget.id)}
                        onSave={() => saveWidget(widget.id)}
                        onTimeRangeChange={range => handleTimeRangeChange(widget.id, range)}
                      />
                    )}
                  </SortableWidget>
                </Grid>
              ))}
            </Grid>
          </SortableContext>
        </DndContext>
      )}

      {modalState.open && (
        <WidgetModal
          existingWidget={modalState.widget}
          onSave={handleWidgetSave}
          onClose={() => setModalState({ open: false })}
        />
      )}
    </Box>
  );
};

export default Dashboard;
